// ============================================================================
// Need a status — the one-time sort (TCSD-0373 ¶4, prototype screen 2).
// Subs marked Active / Pending / Onboarding with no running or booked building.
// FOUR options and no more: Bench · Inactive · Project work · Not mine.
// Each answer is one write, recorded in the audit trail, and the card goes.
//   Bench         → status Ready. Nightly only: refused when their recorded or
//                   stated work is project-only. With no work on record, choosing
//                   Bench records them as nightly (the bench is nightly by ruling).
//   Inactive      → the existing deactivate process.
//   Project work → their kind of work is recorded as project work; stays Active.
//   Not mine      → my markets come off their list, so they show for whoever
//                   covers the rest — or under All TCS when nobody does.
// ============================================================================

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton, useToast } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { refreshReaders } from '../../../shared/save-path';
import { useHubData, inScope, needsAStatus, shortMarkets, type HubSub, type HubScope } from './use-hub-data';
import { markInactive, setSubKind, setSubMarkets, setSubStatus, divisionsFor } from './actions';
import { Chip } from './sheet';

type Answer = 'Bench' | 'Inactive' | 'Project work' | 'Not mine';
const ANSWERS: Answer[] = ['Bench', 'Inactive', 'Project work', 'Not mine'];

export default function NeedsStatusPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const scope: HubScope = params.get('scope') === 'all' ? 'all' : 'mine';
  const data = useHubData();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const effectiveScope: HubScope = data.myMarkets.length === 0 ? 'all' : scope;

  const rows = useMemo(
    () => data.subs.filter((s) => needsAStatus(s) && inScope(s, effectiveScope, data.myMarkets, data.me)),
    [data.subs, effectiveScope, data.myMarkets, data.me],
  );

  async function answer(s: HubSub, a: Answer) {
    setBusy(s.provider_id);
    try {
      if (a === 'Bench') {
        await setSubStatus(s.provider_id, 'Ready', s.kind_source === 'unknown' ? { capabilities: divisionsFor('nightly') } : {});
      } else if (a === 'Inactive') {
        await markInactive(s.provider_id, data.me);
        refreshReaders(['service_providers']);
      } else if (a === 'Project work') {
        await setSubKind(s.provider_id, 'projects');
      } else {
        await setSubMarkets(s.provider_id, s.markets.filter((m) => !data.myMarkets.includes(m)));
      }
      toast.success(`${s.name}: ${a === 'Bench' ? 'on the bench' : a === 'Not mine' ? 'handed to whoever covers that market' : a.toLowerCase()}`);
      data.refetch();
    } catch (e) {
      toast.error(`Could not record that for ${s.name}`, { description: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-3 p-4">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button type="button" onClick={() => navigate('/people/subcontractors')} className="rounded-md border border-tcs-border px-3 py-1 text-sm">
          Done
        </button>
      </div>
      <h1 className="text-xl font-semibold text-tcs-text">Need a status</h1>
      <p className="text-sm text-tcs-text-muted">Marked active, no building. One tap each.</p>

      {data.error ? (
        <ErrorState body={data.error} onRetry={data.refetch} />
      ) : data.loading ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState title="All sorted" body="Every sub in this view has a status." />
      ) : (
        <ul className="space-y-2">
          {rows.map((s) => {
            const benchRefused = s.kind_source !== 'unknown' && !s.does_nightly;
            const notMineOff = !s.markets.some((m) => data.myMarkets.includes(m));
            return (
              <li key={s.provider_id} data-need={s.provider_id} className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
                <div className="text-sm font-semibold text-tcs-text">{s.name}</div>
                <div className="text-xs text-tcs-text-muted">
                  {[shortMarkets(s.markets) || 'no market', s.status !== 'Active' ? s.status : null, s.does_projects && !s.does_nightly ? 'project work on record' : s.does_nightly ? 'nightly work on record' : 'no work on record']
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ANSWERS.map((a) => (
                    <Chip
                      key={a}
                      on={false}
                      disabled={busy === s.provider_id || (a === 'Bench' && benchRefused) || (a === 'Not mine' && notMineOff)}
                      title={
                        a === 'Bench' && benchRefused
                          ? 'The bench is for nightly cleaners — their work is project work only.'
                          : a === 'Not mine' && notMineOff
                            ? 'None of their markets are yours.'
                            : undefined
                      }
                      onClick={() => void answer(s, a)}
                    >
                      {a}
                    </Chip>
                  ))}
                </div>
                {s.kind_source !== 'unknown' && !s.does_nightly && (
                  <p className="mt-1.5 text-xs text-tcs-text-muted">Bench is for nightly cleaners — their work is project work only.</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-tcs-text-muted">
        <b>Bench</b> = nightly cleaner who came through the application (or left on good terms and wants back).{' '}
        <b>Project work</b> = floor techs etc. — stay active, never on the bench. <b>Not mine</b> = goes to whoever covers that market.
      </p>
    </div>
  );
}
